import { useRef, useEffect, useState } from "react";

interface UseAutoResizeTextareaOptions {
  maxHeight?: number;
  minHeight?: number;
  submitOnEnter?: boolean;
}

export const useAutoResizeTextarea = (
  value: string,
  options: UseAutoResizeTextareaOptions = {}
) => {
  const { maxHeight = 120, minHeight = 21, submitOnEnter = true } = options;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleResizeHeight = () => {
    if (textareaRef.current) {
      // 먼저 현재 스크롤 위치 저장
      const scrollTop = textareaRef.current.scrollTop;

      // 높이를 최소값으로 리셋
      textareaRef.current.style.height = `${minHeight}px`;

      // 스크롤 높이 계산
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.min(scrollHeight, maxHeight);

      // 새 높이 적용
      textareaRef.current.style.height = `${newHeight}px`;

      // 스크롤 위치가 있었다면 복원 (단, 최대 높이에 도달했을 때만)
      if (newHeight === maxHeight && scrollTop > 0) {
        textareaRef.current.scrollTop = scrollTop;
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setTimeout(() => {
      handleResizeHeight();
    }, 0);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = `${minHeight}px`;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter가 아닌 경우 early return
    if (e.key !== "Enter") {
      return;
    }

    if (e.shiftKey) {
      // Shift+Enter: 줄바꿈 허용, 이벤트 전파 방지
      e.stopPropagation();
      // 기본 동작(줄바꿈)은 허용하므로 preventDefault 호출하지 않음
      setTimeout(() => {
        handleResizeHeight();
      }, 0);
      return;
    }

    if (!submitOnEnter) {
      return;
    }

    // Enter만: form 제출
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent.isComposing) return;

    // form의 submit을 트리거
    const form = e.currentTarget.closest("form");
    if (form) {
      const submitEvent = new Event("submit", { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
    }
  };

  // value 변경 시 높이 조정
  useEffect(() => {
    if (isFocused) {
      handleResizeHeight();
    }
  }, [value, isFocused]);

  const textareaStyles: React.CSSProperties = {
    boxSizing: "border-box",
    minHeight: `${minHeight}px`,
    maxHeight: `${maxHeight}px`,
    overflow: isFocused ? "auto" : "hidden",
    whiteSpace: isFocused ? "pre-wrap" : "nowrap",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    wordBreak: isFocused ? "break-word" : "normal",
    // blur 상태에서 페이드 아웃 그라데이션 적용
    maskImage: isFocused
      ? "none"
      : "linear-gradient(to right, black 0%, black 80%, transparent 100%)",
    WebkitMaskImage: isFocused
      ? "none"
      : "linear-gradient(to right, black 0%, black 80%, transparent 100%)",
  };

  return {
    textareaRef,
    isFocused,
    handleFocus,
    handleBlur,
    handleKeyDown,
    textareaStyles,
    handleResizeHeight,
  };
};
